import React from "react";
import {
  FormGroup,
  Spinner,
  TextArea,
  Intent,
  Button,
  Callout,
  Radio,
  RadioGroup
} from "@blueprintjs/core";
import styles from "./ThemeSettings.module.scss";
import { ImageUploadArea } from "../../../molecules/ImageUploadArea";
import { Toolbar, ToolbarGroup } from "../../../molecules/Toolbar";
import SettingsTab from "../SettingsTab";
import SettingsForm from "../SettingsForm";
import { FormHeading } from "../../../atoms/FormHeading";
import { FormSection } from "../../../atoms/FormSection";
import { useThemeComponents } from "./hooks";
import { ColorPicker } from "./ColorPicker";
import classnames from "classnames";
import { FormInfo } from "../../../atoms/FormInfo";
import ImageUploadPanel from "./ImageUploadPanel";
import ThemePreviewPage from "./ThemePreviewPage";

export default function ThemeSettings() {
  const {
    isPreviewOpen,
    setIsPreviewOpen,
    brandLogo,
    onLogoChange,
    saveChanges,
    backgroundImage,
    onBackgroundChange,
    backgroundOption,
    setBackgroundOption,
    headingColor,
    setHeadingColor,
    textColor,
    setTextColor,
    bgColor,
    setBgColor,
    loading,
    isSaving,
    error,
    isSaveEnabled,
    onCancel
  } = useThemeComponents();

  if (loading) {
    return (
      <SettingsTab centered>
        <Spinner />
      </SettingsTab>
    );
  }

  return (
    <SettingsTab>
      <Toolbar title="Settings">
        <ToolbarGroup>
          <Button
            text="Cancel"
            className={styles.cancelButton}
            loading={isSaving}
            disabled={isSaving}
            onClick={onCancel}
          />
          <Button
            text="Save"
            intent={Intent.PRIMARY}
            loading={isSaving}
            disabled={!isSaveEnabled}
            onClick={saveChanges}
          />
        </ToolbarGroup>
      </Toolbar>

      <SettingsForm>
        {error && (
          <Callout
            icon="error"
            intent={Intent.DANGER}
            title="We couldn't save your settings."
          >
            {error.message}
          </Callout>
        )}

        <FormHeading weight="600" size={16}>
          Theme
        </FormHeading>

        <FormInfo>
          Please use theme settings to change background and text color for web
          player.
        </FormInfo>

        <div className={styles.brandLogo}>
          <ImageUploadArea
            image={brandLogo}
            onImageChange={onLogoChange}
            title="Upload Brand Logo"
            desc={
              <>
                PNG or JPEG format,
                <br /> 120 x 120 px, 1 MB (or less).
              </>
            }
          />
        </div>
        <FormSection className={styles.colorOptions}>
          <FormSection className={styles.colorRow}>
            <FormInfo>Choose Heading Color</FormInfo>
            <ColorPicker
              className={styles.colorPicker}
              value={headingColor}
              onChange={setHeadingColor}
            />
          </FormSection>
          <FormSection className={styles.colorRow}>
            <FormInfo>Choose Text Color</FormInfo>
            <ColorPicker
              className={styles.colorPicker}
              value={textColor}
              onChange={setTextColor}
            />
          </FormSection>
        </FormSection>

        <RadioGroup
          onChange={setBackgroundOption}
          selectedValue={backgroundOption}
        >
          <Radio
            labelElement={
              <span className={styles.radioLabel}>Choose Background Color</span>
            }
            value="color"
            className={classnames(
              styles.radioOption,
              backgroundOption == "color" && styles.active
            )}
          >
            <ColorPicker
              className={styles.colorPickerBackground}
              value={bgColor}
              onChange={setBgColor}
              disabled={backgroundOption !== "color"}
            />
          </Radio>
          <Radio
            labelElement={
              <span className={styles.radioLabel}>Choose Background Image</span>
            }
            value="image"
            className={classnames(
              styles.radioOption,
              backgroundOption == "image" && styles.active
            )}
          >
            <FormGroup helperText="Adding a video background image is a great way to improve brand awarness.">
              <ImageUploadPanel
                onImageChange={onBackgroundChange}
                image={backgroundImage}
                disabled={backgroundOption !== "image"}
              />
            </FormGroup>
          </Radio>
        </RadioGroup>
        <ThemePreviewPage
          brandLogo={brandLogo}
          backgroundOption={backgroundOption}
          backgroundImage={backgroundImage}
          bgColor={bgColor}
          textColor={textColor}
          headingColor={headingColor}
          isOpen={isPreviewOpen}
          onDismiss={() => setIsPreviewOpen(false)}
        />
        <Button
          text="Preview"
          intent={Intent.PRIMARY}
          disabled={!isSaveEnabled}
          className={styles.previewButton}
          onClick={() => setIsPreviewOpen(true)}
        />
      </SettingsForm>
    </SettingsTab>
  );
}
